import { Fragment, KeyboardEvent, useEffect, useState } from "react";
import { Card, Col, Container, Input, Row } from "reactstrap";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import { useSelector } from "react-redux";
import { RootState } from "../../redux-toolkit/store";
import MonthDropdown from "../CommonCompo/MonthDropdown";
import CommonTableNew, {
  HeadDataItem,
} from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { API } from "../../api/API";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import { Image } from "../../AbstractElements";
import { dynamicImage } from "../../Utils";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import { getFilteredPositionIds } from "../../db/DexieServises";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

type LightboxSlide = {
  src: string;
  downloadUrl?: string;
  downloadFileName?: string;
  [key: string]: unknown;
};

export default function PodReport() {
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const [searchText, setSearchText] = useState("");
  const [podData, setPodData] = useState<any[]>([]);
  const [displayedData, setDisplayedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState<HeadDataItem[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState<LightboxSlide[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const serverUrl = localStorage.getItem("serverUrl");

  const extractFileName = (url: string, fallback: string): string => {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname;
      const name = pathname.split("/").filter(Boolean).pop();
      if (name) {
        return name;
      }
    } catch {
      const cleaned = url.split("?")[0];
      const name = cleaned.split("/").filter(Boolean).pop();
      if (name) {
        return name;
      }
    }
    return fallback;
  };

  const parseInvoiceUrls = (invoiceFiles?: string): string[] => {
    if (!invoiceFiles) {
      return [];
    }

    return invoiceFiles
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const openInvoiceLightbox = (urls: string[], startIndex: number) => {
    if (!urls.length) {
      return;
    }

    const slides = urls.map((url, idx) => {
      const downloadFileName = extractFileName(url, `invoice-${idx + 1}`);
      return {
        src: url,
        downloadUrl: url,
        downloadFileName,
      };
    });

    setLightboxSlides(slides);
    setLightboxIndex(Math.min(Math.max(startIndex, 0), slides.length - 1));
    setLightboxOpen(true);
  };

  const closeInvoiceLightbox = () => setLightboxOpen(false);

  useEffect(() => {
    if (selectedMonth) {
      fetchPodData();
    }
  }, [selectedMonth, unitId, selectedIds]);

  useEffect(() => {
    const fetchPositionIds = async () => {
      if (unitId) {
        const ids = await getFilteredPositionIds("Position", unitId);
        setSelectedIds(ids);
      }
    };

    fetchPositionIds();
  }, [unitId]);

  const fetchPodData = async () => {
    if (!serverUrl) {
      console.error("Server URL is not configured.");
      return;
    }

    setLoading(true);
    const url = `${serverUrl}${API.getPodReport}`;
    const positionIds =
      selectedIds.length > 0
        ? selectedIds
        : await getFilteredPositionIds("Position", unitId);
    const { status, data } = await getData(url, true, {
      moye: selectedMonth,
      position_id: positionIds,
    });

    if (status === 200 && Array.isArray(data?.data)) {
      setPodData(data.data);
      setDisplayedData(data.data);

      // Dynamically set columns based on API response keys
      if (data.data.length > 0) {
        const dynamicColumns: HeadDataItem[] = Object.keys(data.data[0]).map(
          (key, index) => ({
            id: String(index + 1),
            head: key.toString(),
            key: key,
            sortKey: key,
          })
        );

        setColumns(dynamicColumns);
      }
    } else {
      setPodData([]);
      setDisplayedData([]);
    }
    setLoading(false);
  };

  const handleExportToExcel = () => {
    const exportData = displayedData.map((row) => {
      const out: Record<string, any> = {};
      columns.forEach((c) => {
        const key = typeof c.head === "string" ? c.head : c.key;
        out[key] = (row as any)[c.key] ?? "-";
      });
      return out;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Orders_Report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  useEffect(() => {
    const lower = searchText.toLowerCase();
    const filtered = podData.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(lower)
      )
    );
    setDisplayedData(filtered);
  }, [searchText, podData]);

  const staticColumns: HeadDataItem[] = [
    // { id: "1", head: "Unique ID", key: "Uniqueid", sortKey: "Uniqueid" },
    // {
    //   id: "2",
    //   head: "Org Unit Code",
    //   key: "OrgUnitCode",
    //   sortKey: "OrgUnitCode",
    // },
    {
      id: "3",
      head: "Position Code",
      key: "PositionCode",
      sortKey: "PositionCode",
    },
    {
      id: "4",
      head: "Position Name",
      key: "PositionName",
      sortKey: "PositionName",
    },
    { id: "5", head: "Level 1 Code", key: "Level1Code", sortKey: "Level1Code" },
    { id: "6", head: "Level 1 Name", key: "Level1Name", sortKey: "Level1Name" },
    { id: "7", head: "Level 2 Code", key: "Level2Code", sortKey: "Level2Code" },
    { id: "8", head: "Level 2 Name", key: "Level2Name", sortKey: "Level2Name" },
    {
      id: "9",
      head: "Employee Code",
      key: "EmployeeCode",
      sortKey: "EmployeeCode",
    },
    { id: "10", head: "First Name", key: "FirstName", sortKey: "FirstName" },
    { id: "11", head: "Last Name", key: "LastName", sortKey: "LastName" },
    { id: "12", head: "Month Year", key: "MonthYear", sortKey: "MonthYear" },
    { id: "13", head: "Order Dates", key: "OrderDates", sortKey: "OrderDates" },
    { id: "14", head: "Outlet From", key: "OutletFrom", sortKey: "OutletFrom" },
    {
      id: "15",
      head: "From Outlet Name",
      key: "FromoutletName",
      sortKey: "FromoutletName",
    },
    {
      id: "16",
      head: "From Outlet Type",
      key: "FromoutletType",
      sortKey: "FromoutletType",
    },
    {
      id: "17",
      head: "From Outlet Code",
      key: "FromoutletCode",
      sortKey: "FromoutletCode",
    },
    { id: "18", head: "Outlet To", key: "OutletTo", sortKey: "OutletTo" },
    {
      id: "19",
      head: "To Outlet Name",
      key: "TooutletName",
      sortKey: "TooutletName",
    },
    {
      id: "20",
      head: "To Outlet Type",
      key: "TooutletType",
      sortKey: "TooutletType",
    },
    {
      id: "21",
      head: "To Outlet Code",
      key: "TooutletCode",
      sortKey: "TooutletCode",
    },
    { id: "22", head: "Product ID", key: "ProductId", sortKey: "ProductId" },
    { id: "23", head: "Product SKU", key: "ProductSku", sortKey: "ProductSku" },
    { id: "24", head: "Rate", key: "Rate", sortKey: "Rate" },
    { id: "25", head: "POB Qty", key: "PobQty", sortKey: "PobQty" },
    { id: "26", head: "POB Value", key: "PobValue", sortKey: "PobValue" },
    {
      id: "27",
      head: "Submitted POB Qty",
      key: "SubmittedPodQty",
      sortKey: "SubmittedPodQty",
    },
    {
      id: "28",
      head: "Submitted POB Value",
      key: "SubmittedPodValue",
      sortKey: "SubmittedPodValue",
    },
    {
      id: "29",
      head: "Approved POB Qty",
      key: "ApprovedPodQty",
      sortKey: "ApprovedPodQty",
    },
    {
      id: "30",
      head: "Approved POB Value",
      key: "ApprovedPodValue",
      sortKey: "ApprovedPodValue",
    },
    {
      id: "31",
      head: "Rejected POB Qty",
      key: "RejectedPodQty",
      sortKey: "RejectedPodQty",
    },
    {
      id: "32",
      head: "Rejected POB Value",
      key: "RejectedPodValue",
      sortKey: "RejectedPodValue",
    },
    { id: "33", head: "POB Dates", key: "PodDates", sortKey: "PodDates" },
    {
      id: "34",
      head: "Pending POB Qty",
      key: "PendingPodQty",
      sortKey: "PendingPodQty",
    },
    {
      id: "35",
      head: "Pending POB Value",
      key: "PendingPodValue",
      sortKey: "PendingPodValue",
    },
    { id: "36", head: "Company ID", key: "CompanyId", sortKey: "CompanyId" },
    {
      id: "37",
      head: "Invoice Files",
      key: "InvoiceFiles",
      sortKey: "InvoiceFiles",
    },
  ];

  useEffect(() => {
    setColumns(staticColumns);
  }, []);

  return (
    <div className="page-body p-2">
      <Card>
        <div className="d-flex justify-content-between align-items-center pt-2 pb-2">
          <CardHeaderSpan
            headingClassName="pb-0"
            back={true}
            heading="Pod Report"
            span="Detailed Pod Report"
          />
          <div className="p-3">
            <MonthDropdown onChange={(value) => setSelectedMonth(value)} />
          </div>
        </div>
        <div className="row px-3 mb-3 align-items-center justify-content-between">
          <div className="col-md-8 d-flex align-items-center gap-3 flex-wrap">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by name or position"
              className="form-control"
              style={{ maxWidth: "250px" }}
            />
            <TreeModel onSelectionChange={setSelectedIds} />
          </div>

          <div className="col-md-4 d-flex justify-content-end gap-3">
            <button
              className="btn btn-sm btn-primary align-items-center d-flex "
              onClick={handleExportToExcel}
            >
              <FeatherIconCom
                iconName={"Download"}
                size={14}
                className="text-light"
              />
              <span className="px-2" style={{ fontSize: 14 }}>
                Export
              </span>
            </button>
          </div>
        </div>

        <Fragment>
          <Container fluid>
            <Row>
              <Col sm={12}>
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-3">
                      <div>
                        <Image
                          className="b-r-10 img-80"
                          src={dynamicImage("dashboard/loader.gif")}
                          alt="Loading..."
                        />
                      </div>
                    </div>
                  ) : (
                    <CommonTableNew
                      caption=""
                      headData={staticColumns}
                      data={displayedData}
                      renderRow={(row, rowIndex) => {
                        const invoiceUrls = parseInvoiceUrls(row.InvoiceFiles);

                        return (
                          <tr key={rowIndex}>
                            {/* <td>{row.OrgUnitCode ?? "-"}</td> */}
                            <td>{row.PositionCode ?? "-"}</td>
                            <td>{row.PositionName ?? "-"}</td>
                            <td>{row.Level1Code ?? "-"}</td>
                            <td>{row.Level1Name ?? "-"}</td>
                            <td>{row.Level2Code ?? "-"}</td>
                            <td>{row.Level2Name ?? "-"}</td>
                            <td>{row.EmployeeCode ?? "-"}</td>
                            <td>{row.FirstName ?? "-"}</td>
                            <td>{row.LastName ?? "-"}</td>
                            <td>{row.MonthYear ?? "-"}</td>
                            <td>{row.OrderDates ?? "-"}</td>
                            <td>{row.OutletFrom ?? "-"}</td>
                            <td>{row.FromoutletName ?? "-"}</td>
                            <td>{row.FromoutletType ?? "-"}</td>
                            <td>{row.FromoutletCode ?? "-"}</td>
                            <td>{row.OutletTo ?? "-"}</td>
                            <td>{row.TooutletName ?? "-"}</td>
                            <td>{row.TooutletType ?? "-"}</td>
                            <td>{row.TooutletCode ?? "-"}</td>
                            <td>{row.ProductId ?? "-"}</td>
                            <td>{row.ProductSku ?? "-"}</td>
                            <td>{row.Rate ?? "-"}</td>
                            <td>{row.PobQty ?? "-"}</td>
                            <td>{row.PobValue ?? "-"}</td>
                            <td>{row.SubmittedPodQty ?? "-"}</td>
                            <td>{row.SubmittedPodValue ?? "-"}</td>
                            <td>{row.ApprovedPodQty ?? "-"}</td>
                            <td>{row.ApprovedPodValue ?? "-"}</td>
                            <td>{row.RejectedPodQty ?? "-"}</td>
                            <td>{row.RejectedPodValue ?? "-"}</td>
                            <td>{row.PodDates ?? "-"}</td>
                            <td>{row.PendingPodQty ?? "-"}</td>
                            <td>{row.PendingPodValue ?? "-"}</td>
                            <td>{row.CompanyId ?? "-"}</td>
                            <td>
                              {invoiceUrls.length
                                ? invoiceUrls.map((url, index) => (
                                    <span
                                      key={`${url}-${index}`}
                                      style={{
                                        marginRight: "8px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        openInvoiceLightbox(invoiceUrls, index)
                                      }
                                      onKeyDown={(
                                        event: KeyboardEvent<HTMLSpanElement>
                                      ) => {
                                        if (
                                          event.key === "Enter" ||
                                          event.key === " "
                                        ) {
                                          event.preventDefault();
                                          openInvoiceLightbox(invoiceUrls, index);
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`View invoice file ${
                                        index + 1
                                      }`}
                                    >
                                      <FeatherIconCom iconName="Image" size={20} />
                                    </span>
                                  ))
                                : "-"}
                            </td>
                          </tr>
                        );
                      }}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </Fragment>
        <Lightbox
          open={lightboxOpen}
          close={closeInvoiceLightbox}
          slides={lightboxSlides}
          index={lightboxIndex}
          plugins={[Download, Zoom]}
          on={{
            view: ({ index }) => setLightboxIndex(index),
          }}
        />
        {lightboxOpen && lightboxSlides.length > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 999999,
              color: "#fff",
              background: "rgba(0,0,0,0.55)",
              padding: "4px 10px",
              borderRadius: 8,
              fontSize: 14,
              lineHeight: 1.6,
              pointerEvents: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
            aria-live="polite"
          >
            {lightboxIndex + 1} / {lightboxSlides.length}
          </div>
        )}
      </Card>
    </div>
  );
}
